import { Dialog, Link, MessageBar, MessageBarType, PrimaryButton, ProgressIndicator, Stack } from "@fluentui/react"
import { buildColumns, DetailsList, IColumn, SelectionMode } from "@fluentui/react/lib/DetailsList"
import { useCallback, useEffect, useState } from "react"
import { Accordion, AccordionItem, AccordionItemButton, AccordionItemHeading, AccordionItemPanel } from "react-accessible-accordion"
import { read, utils } from "xlsx"
import { makeApiCall, makeCRMApiCall, makeCRMApiCallWithDetails } from "../services/apiService"
import { IAccount } from "../types/IAccount"
import { IEmployeeCreate } from "../types/IEmployee"
import { IHouseholdMemberCreate } from "../types/IHouseholdMember"
import { DragDropFile } from "./drag-drop-file"

import { getRelationshipValueFromFamilyRelationshipString } from "../lib/helper"
import "../styles/accordion.scss"
import { buttonStyles } from "./enrollment-manager"

interface EnrollmentDataRow {
	"Excel Row Number": number
	FamilyID: number
	"First Name": string
	"Middle Name"?: string
	"Last Name": string
	DOB?: string
	Gender: string
	Relationship: string
	Zip: string
	Email?: string
	Phone: string
	Address: string
	City: string
	"State ID": string
}

interface ErrorData {
	Message: string
	Column: keyof EnrollmentDataRow
}

interface EnrollmentDataRowWithErrors extends EnrollmentDataRow {
	Errors: ErrorData[]
}

interface UploadCensusDialogProps {
	closePanel: (refreshItems: boolean, navigateToManageEnrollment: boolean) => void
	group: IAccount
	hideEnrollmentLink?: boolean
}

export function UploadCensusDialog({ closePanel, group, hideEnrollmentLink = false }: UploadCensusDialogProps) {
	const [error, setError] = useState<string | undefined>()
	const [file, setFile] = useState<File>()
	const [fileEnrollmentData, setFileEnrollmentData] = useState<EnrollmentDataRow[]>()
	const [errorRows, setErrorRows] = useState<EnrollmentDataRowWithErrors[]>([])
	const [warningRows, setWarningRows] = useState<EnrollmentDataRowWithErrors[]>([])
	const [errorColumns, setErrorColumns] = useState<string[]>([])
	const [otherFileErrors, setOtherFileErrors] = useState<string>()
	const [censusUploading, setCensusUploading] = useState<boolean>(false)
	const [censusUploaded, setCensusUploaded] = useState<boolean>(false)
	const [censusGroup, setCensusGroup] = useState<IAccount>(group)
	const [clearingSensusLabel, setClearingSensusLabel] = useState<string>()

	const requiredColumns = ["FamilyID", "First Name", "Last Name", "DOB", "Gender", "Relationship", "Zip"]
	const effDate = group.cw_coveragestartdate

	const validateRequiredColumns = (rows: EnrollmentDataRow[]): string[] => {
		const missingColumns = []
		const keys = rows?.length > 0 ? Object.keys(rows[0]) : []
		for (let i = 0; i < requiredColumns.length; i++) {
			const key = requiredColumns[i]
			if (!keys.includes(key)) {
				missingColumns.push(key)
			}
		}
		return missingColumns
	}

	const normalizeData = (rows: EnrollmentDataRow[]) => {
		return rows.map(r => {
			if (
				r.Gender?.toLowerCase() === "m" ||
				r.Gender?.toLowerCase() === "f" ||
				r.Gender?.toLowerCase() === "male" ||
				r.Gender?.toLowerCase() === "female" ||
				r.Gender?.toLowerCase() === "other"
			) {
				if (r.Gender.toLowerCase().startsWith("m")) {
					r.Gender = "MALE"
				} else if (r.Gender.toLowerCase().startsWith("f")) {
					r.Gender = "FEMALE"
				} else {
					r.Gender = "OTHER"
				}
			}
			if (r.DOB) {
				const bd = new Date(Math.round((Number(r.DOB) - 25569) * 86400 * 1000))
				bd.setDate(bd.getDate() + 1)
				r.DOB = bd.toLocaleDateString()
			}
			if (r.Relationship) {
				switch (r.Relationship.toLowerCase()) {
					case "employee":
					case "emp":
					case "sub":
					case "subscriber":
					case "member":
					case "ee":
					case "self":
						r.Relationship = "EMPLOYEE"
						break
					case "spouse":
					case "sp":
						r.Relationship = "SPOUSE"
						break
					case "child":
					case "ch":
					case "dep":
					case "dependant":
						r.Relationship = "CHILD"
						break
				}
			}
			return r
		})
	}

	const getExistingHouseholdMembers = useCallback(async () => {
		const body = {
			query: `/cw_employees?$filter=_cw_employer_value eq '${censusGroup.accountid}'&$expand=cw_cw_householdmembers_Employee_cw_employee($select=cw_householdmembersid)&$select=cw_employeeid`
		}
		const response = await makeApiCall("/makeCRMGetRequest", "POST", JSON.stringify(body))
		const employees = response.value

		censusGroup.employees = employees
		setCensusGroup(censusGroup)
	}, [censusGroup])

	useEffect(() => {
		getExistingHouseholdMembers()
	}, [censusGroup.accountid, getExistingHouseholdMembers])

	const getAge = (dob, referenceDate = new Date()) => {
		const birthDate = new Date(dob)
		const refDate = new Date(referenceDate)

		let age = refDate.getFullYear() - birthDate.getFullYear()
		const monthDiff = refDate.getMonth() - birthDate.getMonth()

		if (monthDiff < 0 || (monthDiff === 0 && refDate.getDate() < birthDate.getDate())) {
			age--
		}

		return age
	}

	useEffect(() => {
		if (file) {
			try {
				const reader = new FileReader()
				reader.onload = event => {
					const wb = read(event.target.result)
					const sheets = wb.SheetNames

					if (sheets.length) {
						let rows = utils.sheet_to_json(wb.Sheets[sheets[0]], { raw: true }) as EnrollmentDataRow[]
						rows = normalizeData(rows).filter(r => Number(r.FamilyID) >= 1)
						const data = rows.map((e, i) => {
							const newData: EnrollmentDataRow = { "Excel Row Number": i + 1, ...e }
							return newData
						})
						validateData(data)
						setFileEnrollmentData(data)
					}
				}
				reader.readAsArrayBuffer(file)
			} catch (err) {
				console.log(err)
				setError("There was an error parsing the spreadsheet")
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [file])

	const validateData = (fileEnrollmentData: EnrollmentDataRow[]) => {
		setErrorRows([])
		setOtherFileErrors(undefined)

		const dobCounts: Record<string, number> = {}
		fileEnrollmentData.forEach(r => {
			if (r.DOB) dobCounts[r.DOB] = (dobCounts[r.DOB] || 0) + 1
		})

		const missingColumns = validateRequiredColumns(fileEnrollmentData)
		const errorRows: EnrollmentDataRowWithErrors[] = []
		if (missingColumns.length === 0) {
			fileEnrollmentData.forEach(r => {
				const errors: ErrorData[] = []
				const warnings: ErrorData[] = []
				if (!r.DOB) {
					errors.push({ Column: "DOB", Message: "Missing DOB" })
				} else if (new Date(r.DOB).toString() === "Invalid Date") {
					errors.push({ Column: "DOB", Message: "Invalid DOB" })
				} else if (dobCounts[r.DOB] > 1) {
					warnings.push({ Column: "DOB", Message: "Duplicate Birthday" })
				}

				if (!r["First Name"]) {
					errors.push({ Column: "First Name", Message: "Missing First Name" })
				}

				if (!r["Last Name"]) {
					errors.push({ Column: "Last Name", Message: "Missing Last Name" })
				}

				if (!r.Gender) {
					errors.push({ Column: "Gender", Message: "Missing Gender" })
				} else if (
					r.Gender.toLowerCase() !== "m" &&
					r.Gender.toLowerCase() !== "f" &&
					r.Gender.toLowerCase() !== "male" &&
					r.Gender.toLowerCase() !== "female" &&
					r.Gender.toLowerCase() !== "other"
				) {
					errors.push({ Column: "Gender", Message: "Invalid Gender" })
				}

				const familyExists = fileEnrollmentData.filter(d => d.FamilyID === r.FamilyID && d["Excel Row Number"] !== r["Excel Row Number"]).length > 0
				const validRelationships = ["employee", "child", "spouse"]
				const age = getAge(r.DOB)
				if (!r.Relationship && familyExists) {
					errors.push({ Column: "Relationship", Message: "Missing Relationship Information" })
				} else if (familyExists && !validRelationships.includes(r.Relationship.toLowerCase())) {
					errors.push({ Column: "Relationship", Message: `Invalid Relationship. Relationship must be: ${validRelationships.join(", ")}` })
				}

				if (r.Relationship.toLowerCase() === "employee" && age < 18) {
					warnings.push({ Column: "DOB", Message: "Employee is less than 18 years old" })
				}
				if (r.Relationship.toLowerCase() === "child" && age > 26) {
					warnings.push({ Column: "DOB", Message: "Child is over 26 years old" })
				}
				if (errors.length > 0) {
					errorRows.push({ Errors: errors, ...r })
				}
				if (warnings.length > 0) {
					warningRows.push({ Errors: warnings, ...r })
				}
			})
			setErrorColumns(errorColumns)
			setErrorRows(errorRows)
			setWarningRows(warningRows)
		} else {
			setOtherFileErrors(`Missing the following columns: ${missingColumns.join(", ")}. Please make sure you are using the correct template.`)
		}
	}

	const getColumns = (items: any[], includeErrorColumns: boolean, warnings?: boolean): IColumn[] => {
		const columns = buildColumns(items)
		const messageBarColor = warnings ? MessageBarType.warning : MessageBarType.error

		columns.forEach(column => {
			if (column.key === "FamilyID") {
				column.maxWidth = 80
			}
			if (column.key === "Email") {
				column.minWidth = 150
			}
			if (column.key === "RowNum") {
				column.maxWidth = 80
			}
			if (column.key === "Errors") {
				column.isMultiline = true
			}
			if (includeErrorColumns) {
				column.onRender = (item: EnrollmentDataRowWithErrors, _, currentColumn) => {
					if (item.Errors && item.Errors.length > 0 && item.Errors.map(e => e.Column).includes(currentColumn.key as keyof EnrollmentDataRow)) {
						return (
							<MessageBar styles={{ iconContainer: { display: "none" } }} messageBarType={messageBarColor} messageBarIconProps={{ iconName: undefined }}>
								{item[currentColumn.fieldName]}
							</MessageBar>
						)
					} else if (currentColumn.key === "Errors") {
						return item.Errors.map(e => e.Message).join(", ")
					} else {
						return item[currentColumn.fieldName]
					}
				}
			}
		})

		return columns
	}

	interface IEmployeeCreateWithFamilyMembers {
		employeeRecordToCreate?: IEmployeeCreate
		familyMembers?: EnrollmentDataRow[]
	}

	const clearCensus = async (group: IAccount) => {
		try {
			setClearingSensusLabel("Clearing existing census data...")
			const houseHoldMemberIds = group.employees.flatMap(e => e.cw_cw_householdmembers_Employee_cw_employee).map(hh => hh.cw_householdmembersid)
			const deleteHH = await Promise.all(houseHoldMemberIds.map(hhId => makeCRMApiCall(`cw_householdmemberses(${hhId})`, "DELETE")))
			await Promise.all(group.employees.map(employee => makeCRMApiCall(`cw_employees(${employee.cw_employeeid})`, "DELETE")))
			return deleteHH
		} catch (error) {
			console.error("Error clearing census:", error)
			setClearingSensusLabel(undefined)
			throw error
		}
	}

	/*
	Emp/Spouse	121640000
	Waived	121640001
	Family	121640002
	Spouse Only	121640003
	Emp/Child	121640004
	Child Only	121640005
	Emp Only	121640007
	Emp/Children	121640008
	*/
	const getHealthCareCoverageType = (familyMembers: EnrollmentDataRow[]): number => {
		let healthCareCoverageType = 121640007 //Emp Only (default)
		if (familyMembers.length > 1) {
			//more than one family member (including employee)
			if (!familyMembers.some(f => f.Relationship === "CHILD")) {
				//no children
				healthCareCoverageType = 121640000 //Emp/Spouse
			} else if (!familyMembers.some(f => f.Relationship === "SPOUSE") && familyMembers.length === 2) {
				//no spouse, one child
				healthCareCoverageType = 121640004 //Emp/Child
			} else if (!familyMembers.some(f => f.Relationship === "SPOUSE") && familyMembers.length > 2) {
				//no spouse, multiple children
				healthCareCoverageType = 121640008 //Emp/Children
			} else {
				healthCareCoverageType = 121640002 //family
			}
		}
		return healthCareCoverageType
	}

	const uploadCensus = async () => {
		setCensusUploading(true)

		let employeesToCreateWithFamilyMembers: IEmployeeCreateWithFamilyMembers[] = []
		fileEnrollmentData.forEach(d => {
			const familyMembers = d.FamilyID ? fileEnrollmentData.filter(o => o.FamilyID === d.FamilyID) : []
			const retVal: IEmployeeCreateWithFamilyMembers = {}

			if (familyMembers.length === 0 || (familyMembers.length > 0 && d.Relationship === "EMPLOYEE")) {
				const healthCareCoverageType = getHealthCareCoverageType(familyMembers)
				const record: IEmployeeCreate = {
					"cw_Employer@odata.bind": `/accounts(${group.accountid})`,
					cw_firstname: d["First Name"],
					cw_lastname: d["Last Name"],
					cw_ssn: d["SSN"]?.toString(),
					cw_email: d.Email,
					cw_zipcode: d.Zip.toString(),
					cw_phone: d.Phone,
					cw_gender: d.Gender === "MALE" ? 121640000 : d.Gender === "FEMALE" ? 121640001 : 121640004,
					cw_healthcarecoveragetype: healthCareCoverageType,
					cw_name: `${d["First Name"]} ${d["Last Name"]}`,
					cw_birthdate: new Date(d.DOB).toISOString(),
					cw_insurancestartdate: effDate,
					cw_address: d.Address,
					cw_city: d.City,
					cw_state: d["State ID"]
				}
				retVal.employeeRecordToCreate = record
			}

			if (familyMembers.length > 0 && d.Relationship === "EMPLOYEE") {
				retVal.familyMembers = familyMembers
			}

			employeesToCreateWithFamilyMembers.push(retVal)
		})

		try {
			await clearCensus(group)
			employeesToCreateWithFamilyMembers = employeesToCreateWithFamilyMembers.filter(e => e.employeeRecordToCreate)

			try {
				const createEmployeeResults = await Promise.all(
					employeesToCreateWithFamilyMembers.map(e => makeCRMApiCallWithDetails("cw_employees", "POST", JSON.stringify(e.employeeRecordToCreate)))
				)
				const createHHMRecords: IHouseholdMemberCreate[] = []
				employeesToCreateWithFamilyMembers.forEach((_, index) => {
					const employeeXhr = createEmployeeResults[index][2]
					const employeeId = employeeXhr.getResponseHeader("EntityId")
					const familyMembers = employeesToCreateWithFamilyMembers[index].familyMembers || []

					familyMembers.forEach(familyMember => {
						const hhRecord: IHouseholdMemberCreate = {
							"cw_Employee@odata.bind": `/cw_employees(${employeeId})`,
							cw_firstname: familyMember["First Name"],
							cw_middlename: familyMember["Middle Name"],
							cw_lastname: familyMember["Last Name"],
							cw_ssn: familyMember["SSN"]?.toString(),
							cw_hhzip: familyMember.Zip?.toString(),
							cw_gender: familyMember.Gender === "MALE" ? 121640000 : 121640001,
							cw_name: `${familyMember["First Name"]} ${familyMember["Last Name"]}`,
							cw_birthdate: new Date(familyMember.DOB).toISOString().substring(0, 10),
							cw_insurancestartdate: effDate,
							cw_relationship: Number(getRelationshipValueFromFamilyRelationshipString(familyMember.Relationship))
						}
						createHHMRecords.push(hhRecord)
					})
				})

				try {
					await Promise.all(createHHMRecords.map(h => makeCRMApiCall("cw_householdmemberses", "POST", JSON.stringify(h))))
				} catch (err) {
					console.error(err)
					await clearCensus(group)
					setError("There was an error creating the household member records")
				} finally {
					setCensusUploading(false)
				}
			} catch (err) {
				console.error(err)
				await clearCensus(group)
				setError("There was an error creating the employee records")
				setCensusUploading(false)
			}

			setCensusUploaded(true)
			setCensusUploading(false)
			await makeCRMApiCall(`accounts(${censusGroup.accountid})`, "PATCH", JSON.stringify({ cw_uploadedcensus: true }))
		} catch (err) {
			console.error(err)
			setError("There was an error querying for existing household member records")
			setCensusUploading(false)
		}
	}

	return (
		<Dialog
			onDismiss={() => closePanel(censusUploaded, false)}
			styles={{
				main: {
					width: "calc(100vw - 180px) !important",
					minWidth: "calc(100vw - 180px) !important",
					maxWidth: "calc(100vw - 180px) !important",
					height: "calc(100vh - 100px)"
				}
			}}
			hidden={false}
			dialogContentProps={{
				title: (
					<Stack horizontalAlign="center">
						<h3 style={{ marginTop: 0 }}>Upload Census: {group.name}</h3>
					</Stack>
				)
			}}
			modalProps={{
				isBlocking: true
			}}>
			<Stack tokens={{ childrenGap: 10 }} verticalAlign="center">
				{!censusUploaded && (
					<Stack horizontalAlign="center">
						{!censusUploading && (
							<>
								<div>
									Please <a href="acmg_census_template2024.xlsx">download</a> and use this template. Don&apos;t change the worksheet name. It should always remain Sheet1.
								</div>
								<br />
							</>
						)}
						{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
						{!censusUploading && group.employees?.length > 0 && (
							<MessageBar styles={{ root: { margin: 6 } }} messageBarType={MessageBarType.warning}>
								WARNING: This group already has {group.employees.length} employees. By continuing to upload a census file, you will be overwriting the existing employees and household
								member data.
							</MessageBar>
						)}
						{!file && !censusUploading && (
							<DragDropFile
								multiple={false}
								acceptedFiles={["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"]}
								onFilesDropped={files => {
									const file = files[0]
									setError(undefined)
									if (file.name.toLowerCase().endsWith(".xlsx")) {
										setFile(file)
									} else {
										setError("Only .xlsx files are accepted")
									}
								}}
							/>
						)}
						{file && !censusUploading && (
							<Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
								<span>{file.name}</span>
								<PrimaryButton
									text="Remove and Upload a New File"
									onClick={() => {
										setFile(undefined)
										setFileEnrollmentData(undefined)
									}}
								/>
							</Stack>
						)}
					</Stack>
				)}
				{fileEnrollmentData && !censusUploaded && (
					<Stack>
						<Accordion allowMultipleExpanded allowZeroExpanded={false} preExpanded={["error-rows"]}>
							{!otherFileErrors && (
								<AccordionItem uuid="normalized-rows">
									<AccordionItemHeading>
										<AccordionItemButton>Normalized Data {errorRows.length === 0 && "(Expand to view)"}</AccordionItemButton>
									</AccordionItemHeading>
									<AccordionItemPanel>
										<DetailsList items={fileEnrollmentData} columns={getColumns(fileEnrollmentData, false)} selectionMode={SelectionMode.none} />
									</AccordionItemPanel>
								</AccordionItem>
							)}
							{otherFileErrors && (
								<MessageBar messageBarType={MessageBarType.error}>
									{otherFileErrors} <b>Please correct the errors and re-upload the file by using the button above to remove and re-upload the file.</b>
								</MessageBar>
							)}
							{file && errorRows.length > 0 && (
								<AccordionItem uuid="error-rows">
									<AccordionItemHeading>
										<AccordionItemButton>Error Data</AccordionItemButton>
									</AccordionItemHeading>
									<AccordionItemPanel>
										<Stack>
											<MessageBar messageBarType={MessageBarType.blocked}>
												{errorRows.length} errors were found in the file.{" "}
												<b>Please correct the errors and re-upload the file by using the button above to remove and re-upload the file.</b>
											</MessageBar>
											<DetailsList items={errorRows} columns={getColumns(errorRows, true)} selectionMode={SelectionMode.none} />
										</Stack>
									</AccordionItemPanel>
								</AccordionItem>
							)}
							{file && errorRows.length == 0 && warningRows.length > 0 && (
								<AccordionItem uuid="warning-rows">
									<AccordionItemHeading>
										<AccordionItemButton>Warning Data</AccordionItemButton>
									</AccordionItemHeading>
									<AccordionItemPanel>
										<Stack>
											<DetailsList items={warningRows} columns={getColumns(warningRows, true, true)} selectionMode={SelectionMode.none} />
										</Stack>
									</AccordionItemPanel>
								</AccordionItem>
							)}
						</Accordion>
					</Stack>
				)}
				{!censusUploaded && !censusUploading && !otherFileErrors && file && errorRows.length === 0 && (
					<Stack horizontalAlign="center" tokens={{ childrenGap: 10 }}>
						{!censusUploading && !error && warningRows.length === 0 ? (
							<MessageBar messageBarType={MessageBarType.success}>Great - there were no errors. After reviewing the data, click the button below to upload the census.</MessageBar>
						) : (
							<MessageBar messageBarType={MessageBarType.warning}>
								The data is good to go, but we have flagged the above rows for possible inaccuracies or duplicates. Please verify there are no errors before uploading.
							</MessageBar>
						)}
						<PrimaryButton styles={buttonStyles} text="Upload Census" onClick={uploadCensus} disabled={censusUploading || !censusGroup.employees} />
					</Stack>
				)}
				{!censusUploaded && censusUploading && <ProgressIndicator label={clearingSensusLabel || "Uploading census..."} />}
				{censusUploaded && (
					<MessageBar messageBarType={MessageBarType.success}>
						The employees and household members were successfully processed.
						{!hideEnrollmentLink && (
							<>
								{" "}
								<Link onClick={() => closePanel(true, true)}>Click here</Link> to manage the enrollment.
							</>
						)}
					</MessageBar>
				)}
			</Stack>
		</Dialog>
	)
}
